<template>
  <Teleport to="body">
    <Transition name="player">
      <div v-if="album" class="player-overlay" @click.self="close">
        <div class="player-card">
          <button class="close-btn" @click="close" aria-label="Close player">
            <FontAwesomeIcon icon="fa-solid fa-xmark" />
          </button>

          <img :src="album.albumArtUrl" class="player-album-art" />

          <h2 class="album-title">{{ album.description }}</h2>

          <div v-if="hasInstrumentals" class="version-toggle">
            <button
              :class="['version-btn', { active: versionMode === 'full' }]"
              @click="setVersion('full')"
            >
              Full
            </button>
            <button
              :class="['version-btn', { active: versionMode === 'instrumental' }]"
              @click="setVersion('instrumental')"
            >
              Instrumental
            </button>
          </div>

          <template v-if="hasPlayableTracks">
            <p class="now-playing" v-if="currentTrack">
              {{ currentTrack.name }}
            </p>

            <div class="progress-container" @click="seek">
              <div class="progress-bar">
                <div
                  class="progress-fill"
                  :style="{ width: progressPercent + '%' }"
                ></div>
              </div>
              <div class="time-display">
                <span>{{ formatTime(currentTime) }}</span>
                <span>{{ formatTime(duration) }}</span>
              </div>
            </div>

            <div class="playback-controls">
              <button
                class="control-btn"
                @click="prevTrack"
                aria-label="Previous track"
              >
                <FontAwesomeIcon icon="fa-solid fa-backward-step" />
              </button>
              <button
                class="control-btn play-btn"
                @click="togglePlay"
                aria-label="Play or pause"
              >
                <FontAwesomeIcon
                  :icon="
                    isPlaying ? 'fa-solid fa-pause' : 'fa-solid fa-play'
                  "
                />
              </button>
              <button
                class="control-btn"
                @click="nextTrack"
                aria-label="Next track"
              >
                <FontAwesomeIcon icon="fa-solid fa-forward-step" />
              </button>
            </div>
          </template>

          <div class="track-list" v-if="hasTracks">
            <div
              v-for="(track, index) in album.tracks"
              :key="index"
              class="track-item"
              :class="{ active: currentTrackIndex === index && isPlaying, unavailable: track.unavailable }"
            >
              <button
                class="track-play-area"
                @click="playTrack(index)"
                :disabled="!trackSrc(track)"
              >
                <span class="track-number">{{ index + 1 }}.</span>
                <span class="track-name">{{ track.name }}</span>
              </button>
              <a
                v-if="trackDownloadSrc(track)"
                :href="trackDownloadSrc(track)"
                class="track-download-btn"
                target="_blank"
                download
                @click.stop="onTrackDownload(track)"
              >
                <FontAwesomeIcon icon="fa-solid fa-download" />
              </a>
            </div>
          </div>

          <a
            v-if="album.url"
            :href="album.url"
            class="download-btn"
            target="_blank"
            @click="$emit('download', album.description)"
          >
            <FontAwesomeIcon icon="fa-solid fa-download" />
            <span>Download Album</span>
          </a>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { Howl } from "howler";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
  faXmark,
  faPlay,
  faPause,
  faBackwardStep,
  faForwardStep,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";

library.add(faXmark, faPlay, faPause, faBackwardStep, faForwardStep, faDownload);

export default {
  name: "MusicPlayerCard",
  components: { FontAwesomeIcon },
  props: {
    album: { type: Object, default: null },
  },
  emits: ["close", "download"],

  data() {
    return {
      howl: null,
      isPlaying: false,
      currentTrackIndex: 0,
      currentTime: 0,
      duration: 0,
      versionMode: "full",
    };
  },

  computed: {
    hasTracks() {
      return this.album?.tracks?.length > 0;
    },
    hasPlayableTracks() {
      return (
        this.album?.tracks?.some((t) => t.src || t.instrumentalSrc) ?? false
      );
    },
    currentTrack() {
      if (!this.hasTracks) return null;
      return this.album.tracks[this.currentTrackIndex];
    },
    hasInstrumentals() {
      return this.album?.tracks?.some((t) => t.instrumentalSrc) ?? false;
    },
    progressPercent() {
      if (!this.duration) return 0;
      return (this.currentTime / this.duration) * 100;
    },
  },

  watch: {
    album(newVal, oldVal) {
      if (newVal) {
        document.body.style.overflow = "hidden";
        if (oldVal) this.stopPlayback();
        this.resetPlayer();
      } else {
        document.body.style.overflow = "";
        this.stopPlayback();
      }
    },
  },

  mounted() {
    this._handleEscape = (e) => {
      if (e.key === "Escape" && this.album) this.close();
    };
    document.addEventListener("keydown", this._handleEscape);
  },

  beforeUnmount() {
    document.removeEventListener("keydown", this._handleEscape);
    document.body.style.overflow = "";
    this.stopPlayback();
  },

  methods: {
    close() {
      this.$emit("close");
    },

    resetPlayer() {
      this.currentTrackIndex = 0;
      this.currentTime = 0;
      this.duration = 0;
      this.isPlaying = false;
      this.versionMode = "full";
    },

    stopPlayback() {
      this._cancelProgressLoop();
      if (this.howl) {
        this.howl.unload();
        this.howl = null;
      }
      this.isPlaying = false;
    },

    trackSrc(track) {
      if (this.versionMode === "instrumental" && track.instrumentalSrc) {
        return track.instrumentalSrc;
      }
      return track.src;
    },

    trackDownloadSrc(track) {
      if (this.versionMode === "instrumental") {
        return track.instrumentalDownloadSrc || track.instrumentalSrc;
      }
      return track.downloadSrc || track.src;
    },

    setVersion(mode) {
      if (mode === this.versionMode) return;
      this.versionMode = mode;
      if (!this.howl) return;

      const seekPos = this.howl.seek() || 0;
      const wasPlaying = this.isPlaying;
      this.playTrack(this.currentTrackIndex, seekPos, wasPlaying);
    },

    playTrack(index, seekTo, autoPlay) {
      const track = this.album.tracks[index];
      this.currentTrackIndex = index;
      const src = this.trackSrc(track);
      if (!src) {
        this.isPlaying = false;
        return;
      }

      if (seekTo === undefined) {
        this.$gtag.event("track_play", {
          album: this.album.description,
          track: track.name,
          version: this.versionMode,
        });
      }

      if (this.howl) {
        this.howl.unload();
      }

      const shouldPlay = autoPlay !== false;

      this.howl = new Howl({
        src: [src],
        html5: true,
        onload: () => {
          this.duration = this.howl.duration();
          if (seekTo > 0) {
            this.howl.seek(seekTo);
            this.currentTime = seekTo;
          }
        },
        onplay: () => {
          this.isPlaying = true;
          this._startProgressLoop();
        },
        onpause: () => {
          this.isPlaying = false;
          this._cancelProgressLoop();
        },
        onend: () => {
          this._cancelProgressLoop();
          const len = this.album.tracks.length;
          for (let i = 1; i < len; i++) {
            const idx = this.currentTrackIndex + i;
            if (idx >= len) break;
            if (this.trackSrc(this.album.tracks[idx])) {
              this.playTrack(idx);
              return;
            }
          }
          this.isPlaying = false;
        },
        onloaderror: () => {
          this.isPlaying = false;
        },
        onplayerror: () => {
          this.isPlaying = false;
        },
      });

      if (shouldPlay) this.howl.play();
    },

    togglePlay() {
      if (this.isPlaying && this.howl) {
        this.howl.pause();
        return;
      }

      if (this.howl && !this.isPlaying) {
        this.howl.play();
        return;
      }

      const idx = this.trackSrc(this.currentTrack)
        ? this.currentTrackIndex
        : this.album.tracks.findIndex((t) => this.trackSrc(t));
      if (idx >= 0) this.playTrack(idx);
    },

    prevTrack() {
      if (!this.hasTracks) return;
      const len = this.album.tracks.length;
      for (let i = 1; i < len; i++) {
        const idx = (this.currentTrackIndex - i + len) % len;
        if (this.trackSrc(this.album.tracks[idx])) {
          this.playTrack(idx);
          return;
        }
      }
    },

    nextTrack() {
      if (!this.hasTracks) return;
      const len = this.album.tracks.length;
      for (let i = 1; i < len; i++) {
        const idx = (this.currentTrackIndex + i) % len;
        if (this.trackSrc(this.album.tracks[idx])) {
          this.playTrack(idx);
          return;
        }
      }
    },

    onTrackDownload(track) {
      this.$gtag.event("track_download", {
        album: this.album.description,
        track: track.name,
        version: this.versionMode,
      });
    },

    seek(event) {
      if (!this.howl || !this.duration) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const percent = Math.max(
        0,
        Math.min(1, (event.clientX - rect.left) / rect.width)
      );
      this.howl.seek(percent * this.duration);
      this.currentTime = percent * this.duration;
    },

    _startProgressLoop() {
      this._cancelProgressLoop();
      const update = () => {
        if (this.howl && this.howl.playing()) {
          this.currentTime = this.howl.seek() || 0;
          this.duration = this.howl.duration() || 0;
        }
        this._raf = requestAnimationFrame(update);
      };
      this._raf = requestAnimationFrame(update);
    },

    _cancelProgressLoop() {
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
    },

    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return "0:00";
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    },
  },
};
</script>

<style scoped>
.player-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  backdrop-filter: blur(4px);
  font-family: "Poppins", sans-serif;
}

.player-card {
  position: relative;
  background: #fcefd6;
  border-radius: 20px;
  padding: 32px 28px 24px;
  max-width: 520px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.close-btn {
  position: absolute;
  top: 12px;
  right: 14px;
  background: none;
  border: none;
  font-size: 1.25em;
  color: #8a7e72;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
  transition: color 0.2s, background 0.2s;
}

.close-btn:hover {
  color: #3f3a35;
  background: rgba(0, 0, 0, 0.06);
}

.player-album-art {
  width: 200px;
  height: 200px;
  object-fit: cover;
  border-radius: 14px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
  margin-bottom: 16px;
}

.album-title {
  font-size: 1.2em;
  font-weight: 600;
  color: #3f3a35;
  margin: 0 0 4px;
  letter-spacing: -0.01em;
}

.version-toggle {
  display: flex;
  background: #e8dcc8;
  border-radius: 10px;
  padding: 3px;
  margin: 8px 0 12px;
  gap: 2px;
}

.version-btn {
  flex: 1;
  padding: 6px 16px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-family: inherit;
  font-size: 0.82em;
  font-weight: 500;
  color: #8a7e72;
  cursor: pointer;
  transition: all 0.2s ease;
}

.version-btn.active {
  background: #6b5290;
  color: white;
  box-shadow: 0 1px 4px rgba(107, 82, 144, 0.3);
}

.version-btn:not(.active):hover {
  color: #3f3a35;
}

.now-playing {
  font-size: 0.85em;
  color: #6b5290;
  margin: 0 0 12px;
  font-weight: 500;
}

.progress-container {
  width: 100%;
  padding: 0 4px;
  cursor: pointer;
  margin-bottom: 8px;
}

.progress-bar {
  height: 5px;
  background: #e8dcc8;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #6b5290;
  border-radius: 3px;
  transition: width 0.1s linear;
}

.time-display {
  display: flex;
  justify-content: space-between;
  font-size: 0.72em;
  color: #8a7e72;
  margin-top: 4px;
  font-variant-numeric: tabular-nums;
}

.playback-controls {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 8px 0 16px;
}

.control-btn {
  background: none;
  border: none;
  color: #3f3a35;
  font-size: 1.1em;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: color 0.2s, background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.control-btn:hover {
  color: #6b5290;
  background: rgba(107, 82, 144, 0.08);
}

.play-btn {
  font-size: 1.4em;
  background: #6b5290;
  color: white;
  width: 48px;
  height: 48px;
}

.play-btn:hover {
  background: #5a4479;
  color: white;
}

.track-list {
  width: 100%;
  margin: 4px 0 16px;
  max-height: 260px;
  overflow-y: auto;
}

.track-item {
  display: flex;
  align-items: center;
  width: 100%;
  border-radius: 8px;
  transition: background 0.15s;
  font-size: 0.88em;
  color: #3f3a35;
}

.track-item:hover {
  background: rgba(107, 82, 144, 0.08);
}

.track-item.active {
  background: rgba(107, 82, 144, 0.12);
  color: #6b5290;
  font-weight: 600;
}

.track-play-area {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  padding: 8px 0 8px 12px;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  color: inherit;
  text-align: left;
}

.track-play-area:disabled {
  cursor: default;
  opacity: 0.55;
}

.track-item.unavailable {
  opacity: 0.4;
  pointer-events: none;
}

.track-item.unavailable .track-name {
  font-style: italic;
}

.track-number {
  width: 24px;
  flex-shrink: 0;
  color: #8a7e72;
  font-size: 0.9em;
}

.track-item.active .track-number {
  color: #6b5290;
}

.track-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.track-download-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  color: #8a7e72;
  font-size: 0.8em;
  border-radius: 6px;
  transition: color 0.2s, background 0.2s;
  margin-right: 4px;
}

.track-download-btn:hover {
  color: #6b5290;
  background: rgba(107, 82, 144, 0.1);
}

.track-item.active .track-download-btn {
  color: #6b5290;
}

.download-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  background: #6b5290;
  color: white;
  border: none;
  border-radius: 10px;
  font-family: inherit;
  font-size: 0.9em;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.2s;
  margin-top: 4px;
}

.download-btn:hover {
  background: #5a4479;
  color: white;
}

/* Transition */
.player-enter-active,
.player-leave-active {
  transition: opacity 0.25s ease;
}

.player-enter-active .player-card,
.player-leave-active .player-card {
  transition: transform 0.25s ease;
}

.player-enter-from,
.player-leave-to {
  opacity: 0;
}

.player-enter-from .player-card,
.player-leave-to .player-card {
  transform: translateY(16px) scale(0.97);
}

.track-list::-webkit-scrollbar {
  width: 4px;
}

.track-list::-webkit-scrollbar-track {
  background: transparent;
}

.track-list::-webkit-scrollbar-thumb {
  background: #d4c9b8;
  border-radius: 2px;
}

@media (max-width: 480px) {
  .player-card {
    padding: 24px 20px 20px;
    border-radius: 16px;
  }

  .player-album-art {
    width: 160px;
    height: 160px;
  }
}
</style>
